import { LegalEntityCountry } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { ComponentProps, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Inline } from '../../../design-system/inline/inline';
import { PdfViewer } from '../../../design-system/pdf-viewer/pdf-viewer';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';
import { getAmountWithCurrency } from '../../../utils/get-amount-with-currency';
import { PaymentsFlowContentWrapper } from '../components/payments-flow-content-wrapper';
import { PaymentDetails } from '../payment-flow-storage';
import { usePaymentFlow } from '../use-payment-flow';
import { BillPayFlowsWrapper } from './components/bill-pay-flows-wrapper';
import { SuccessStep } from './components/success-step';
import { getBillPayFlow } from './utils/get-bill-pay-flow';

export type BillPayFlowStep = 'FLOW' | 'SUCCESS';

type BillPayFlowProps = {
  step: BillPayFlowStep;
  evidence: ComponentProps<typeof BillPayFlowsWrapper>['evidence'];
  accounts: ComponentProps<typeof BillPayFlowsWrapper>['accounts'];
  beneficiaries: ComponentProps<typeof BillPayFlowsWrapper>['beneficiaries'];
  companyCountry: LegalEntityCountry;
  xeroCardConfig: ComponentProps<typeof PaymentsFlowContentWrapper>['xeroCardConfig'];
  isLoading: ComponentProps<typeof BillPayFlowsWrapper>['isLoading'];
  isAIEnrichingEvidence: ComponentProps<typeof BillPayFlowsWrapper>['isAIEnrichingEvidence'];
  onCreateBeneficiary: ComponentProps<typeof BillPayFlowsWrapper>['onCreateBeneficiary'];
  onMakePayment: (paymentDetails: PaymentDetails) => Promise<void>;
  onMakeNewPayment: () => void;
  onCancel: () => void;
  onSuccessfulAuthentication: () => void;
  onFailedAuthentication: () => void;
};

export const BillPayFlow = ({
  step,
  evidence,
  accounts,
  beneficiaries,
  companyCountry,
  xeroCardConfig,
  isLoading,
  isAIEnrichingEvidence,
  onCreateBeneficiary,
  onMakePayment,
  onMakeNewPayment,
  onCancel,
  onSuccessfulAuthentication,
  onFailedAuthentication,
}: BillPayFlowProps) => {
  const {
    getPaymentDetails,
    authorisationStatus,
    getDocumentDetails,
    billPayFlow: initialBillPayFlow,
  } = usePaymentFlow();
  const navigate = useNavigate();
  const hasShownErrorRef = useRef(false);

  // Cached because it is a dependency of useEffect
  const cachedOnSuccessfulAuthentication = useCallback(() => {
    onSuccessfulAuthentication();
  }, [onSuccessfulAuthentication]);

  const cachedOnFailedAuthentication = useCallback(() => {
    onFailedAuthentication();
  }, [onFailedAuthentication]);

  useEffect(() => {
    if (step === 'FLOW' && authorisationStatus === 'success') {
      cachedOnSuccessfulAuthentication();

      return;
    }

    if (step === 'FLOW' && authorisationStatus === 'error' && !hasShownErrorRef.current) {
      toast.error('Authentication failed. Please try again.');
      cachedOnFailedAuthentication();

      // Avoid showing the error toaster multiple times
      hasShownErrorRef.current = true;

      return;
    }
  }, [authorisationStatus, cachedOnSuccessfulAuthentication, cachedOnFailedAuthentication, step]);

  // We use getters rather than the state shared by use-payment-flow to avoid closure issues when the state is updated
  const documentDetails = getDocumentDetails();

  switch (step) {
    case 'FLOW': {
      const billPayFlow = initialBillPayFlow ?? getBillPayFlow(evidence);

      if (!documentDetails?.fileName || !documentDetails?.documentUrl) {
        Sentry.captureMessage('Invoice file name or document URL is not available on invoice upload', {
          extra: {
            selectedEvidenceId: evidence?.id,
            evidence,
            step,
            billPayFlow,
            message: 'Invoice file name or document URL is not available on invoice upload',
          },
        });

        return null;
      }

      Sentry.addBreadcrumb({
        data: {
          step,
          initialBillPayFlow,
          getBillPayFlowResult: getBillPayFlow(evidence),
          billPayFlow,
          evidenceId: evidence?.id,
          message: `In bill-pay-flow, 'FLOW' step`,
        },
      });

      return (
        <PaymentsFlowContentWrapper isFullScreenWidth>
          <Inline space={5} wrap="nowrap" className="h-full w-full">
            <div className="h-full flex-1">
              <BillPayFlowsWrapper
                billPayFlow={billPayFlow}
                evidence={evidence}
                accounts={accounts}
                beneficiaries={beneficiaries}
                companyCountry={companyCountry}
                isLoading={isLoading}
                isAIEnrichingEvidence={isAIEnrichingEvidence}
                onCreateBeneficiary={onCreateBeneficiary}
                onMakePayment={onMakePayment}
                onCancelTransfer={onCancel}
              />
            </div>

            <div className="h-full w-1/2 rounded-lg bg-white px-5 py-4">
              <PdfViewer
                fileName={documentDetails.fileName}
                url={documentDetails.documentUrl}
                onDelete={onCancel}
                onUploadNew={onCancel}
              />
            </div>
          </Inline>
        </PaymentsFlowContentWrapper>
      );
    }
    case 'SUCCESS': {
      const paymentDetails = getPaymentDetails();

      if (
        !paymentDetails?.amount ||
        !paymentDetails?.currency ||
        !paymentDetails?.reference ||
        !paymentDetails?.beneficiaryId
      ) {
        Sentry.captureMessage('Payment details are not set in success step in BillPayFlow', {
          extra: {
            paymentDetails,
            billPayStep: step,
            evidenceId: evidence?.id,
            step,
            message: 'Payment details are not set in success step in BillPayFlow',
          },
        });

        return null;
      }

      const { amount, currency, reference, beneficiaryId } = paymentDetails;

      const selectedBeneficiary = beneficiaries.find((b) => b.id === beneficiaryId);

      if (!selectedBeneficiary) {
        Sentry.captureMessage(
          'Selected beneficiary cannot be found among the list of beneficiaries when paying an invoice in success step in BillPayFlow',
          {
            extra: {
              selectedBeneficiaryId: beneficiaryId,
              beneficiaries: beneficiaries.map((b) => b.id),
              step,
              message:
                'Selected beneficiary cannot be found among the list of beneficiaries when paying an invoice in success step in BillPayFlow',
            },
          },
        );

        return null;
      }

      if (!documentDetails?.fileName) {
        Sentry.captureMessage('File name is not available on bill pay success screen', {
          extra: {
            selectedEvidenceId: evidence?.id,
            evidence,
            step,
            message: 'File name is not available on bill pay success screen',
          },
        });

        return null;
      }

      return (
        <PaymentsFlowContentWrapper xeroCardConfig={xeroCardConfig}>
          <SuccessStep
            recipientName={selectedBeneficiary.name}
            amount={getAmountWithCurrency({ amount, currency, isAccountBalance: true })}
            reference={reference}
            fileName={documentDetails.fileName}
            onViewPayment={() => {
              onCancel();

              navigate('/dashboard/transactions');
            }}
            onMakeNewPayment={onMakeNewPayment}
          />
        </PaymentsFlowContentWrapper>
      );
    }
    default:
      return exhaustiveCheck(step);
  }
};
